import { OrderRow } from './OrderRow';
import { useTranslate } from '@tolgee/react';
import { TradeDetailLogisticsBlock } from 'views/Conversation/Sider/useTradeDetailBusiness';
import { Button, Divider, Drawer } from 'antd';
import { useCallback, useContext, useMemo } from 'react';
import { LightChatSideContext } from 'components/LightConversation/contexts/LightChatSide';
import { LogisticsInfo } from '../LogisticsInfo';
import { CloseOutlined } from '@ant-design/icons';
import { useToggle } from 'ahooks';
import { formatLogisticsInfo } from '../LogisticsInfo/utils';
import { MediaViewer, MediaItem } from 'components/MediaViewer';
import { LogisticsImageSendButton } from 'views/Conversation/Sider/LogisticsImageSendButton';

interface ThirdPartyOrderLogisticsProps {
  conversation?: Conversation.Conversation;
  title: string;
  statusLabel: string;
  data: TradeDetailLogisticsBlock['payload'];
  sendable: boolean;
}

export const ThirdPartyOrderLogistics = (props: ThirdPartyOrderLogisticsProps) => {
  const { conversation, title, statusLabel, data, sendable } = props;
  const { t } = useTranslate();
  const [visible, { toggle: toggleVisible }] = useToggle(false);
  const contextValue = useContext(LightChatSideContext);

  const getDrawerContainer = useCallback((): HTMLElement => {
    if (contextValue?.containerRef.current) {
      return contextValue.containerRef.current;
    }
    return document.body;
  }, [contextValue]);

  const isHisense = data.kind === 'hisense';
  const logisticsList = isHisense ? data.logisticsList : undefined;

  const mediaItems: MediaItem[] | undefined = useMemo(() => {
    if (!isHisense || !logisticsList) return undefined;
    return logisticsList
      .flatMap((logistics) => logistics.trackingInfo || [])
      .flatMap((tracking) => tracking.attachment || [])
      .map((item) => ({
        type: 'image' as const,
        src: item.url,
        thumb: item.url,
      }))
      .filter((item) => item.src);
  }, [isHisense, logisticsList]);

  const extraNode = (
    <div className="flex items-center gap-2 mt-1">
      {isHisense && mediaItems && mediaItems.length > 0 && (
        <>
          <MediaViewer
            items={mediaItems}
            trigger={
              <Button type="link" size="small" className="!p-0">
                {t('scene.chatWorkspace.orderTab.logistics.action.viewShippingPhotos')}
              </Button>
            }
            toolbarRender={({ images, index, onClose }) => {
              const item = images[index];
              if (item.src && sendable) {
                return <LogisticsImageSendButton imageUrl={item.src} onClose={onClose} />;
              }
            }}
          />
          <Divider type="vertical" className="!mx-0 !h-[14px]" />
        </>
      )}
      <Button type="link" size="small" className="!p-0" onClick={toggleVisible}>
        {t('sidebar.order.action.detail')}
      </Button>
    </div>
  );

  return (
    <>
      <OrderRow title={title} value={statusLabel} extra={extraNode} />
      <Drawer
        title={
          <span className="text-[14px] font-medium text-[var(--fett-text-primary)]">
            {t('trade.logisticsInfo')}
          </span>
        }
        className="!absolute"
        placement="bottom"
        height="60vh"
        visible={visible}
        onClose={toggleVisible}
        destroyOnClose
        getContainer={getDrawerContainer}
        closable={false}
        headerStyle={{
          padding: '16px 16px 12px 16px',
          border: 'none',
        }}
        bodyStyle={{
          padding: '0 16px 16px 16px',
        }}
        extra={
          <Button type="text" size="small" onClick={toggleVisible}>
            <CloseOutlined />
          </Button>
        }
      >
        <LogisticsInfo
          conversation={conversation}
          list={formatLogisticsInfo(data)}
          sendable={sendable}
          showAll
        />
      </Drawer>
    </>
  );
};
